import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Format a date string to a readable format
 * @param {string} dateString - ISO date string
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    const defaultOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options
    };
    return date.toLocaleDateString('en-US', defaultOptions);
  } catch {
    return 'Invalid Date';
  }
};

/**
 * Format a date to relative time (e.g., "2 days ago")
 * @param {string} dateString - ISO date string
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return 'Never';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 365) return `${Math.floor(diffDays / 365)} year(s) ago`;
    if (diffDays > 30) return `${Math.floor(diffDays / 30)} month(s) ago`;
    if (diffDays > 0) return `${diffDays} day(s) ago`;
    if (diffHours > 0) return `${diffHours} hour(s) ago`;
    if (diffMins > 0) return `${diffMins} minute(s) ago`;
    return 'Just now';
  } catch {
    return 'Unknown';
  }
};

/**
 * Get initials from a name
 * @param {string} name - Full name
 * @returns {string} Initials (max 2 characters)
 */
export const getInitials = (name) => {
  if (!name) return '??';
  
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * Truncate a string to a specified length
 * @param {string} str - String to truncate
 * @param {number} length - Maximum length
 * @returns {string} Truncated string with ellipsis if needed
 */
export const truncate = (str, length = 30) => {
  if (!str) return '';
  if (str.length <= length) return str;
  return `${str.substring(0, length)}...`;
};

/**
 * Capitalize first letter of a string
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
export const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Format identity type to readable label
 * @param {string} type - Identity type
 * @returns {string} Formatted type label
 */
export const formatType = (type) => {
  if (!type) return '';
  return type
    .split('-')
    .map(word => capitalizeFirst(word))
    .join(' ');
};

/**
 * Get CSS class name for risk level
 * @param {string} riskLevel - Risk level
 * @returns {string} CSS class name
 */
export const getRiskClassName = (riskLevel) => {
  const classMap = {
    low: 'success',
    medium: 'warning',
    high: 'danger',
    critical: 'danger'
  };
  return classMap[riskLevel] || 'default';
};

/**
 * Get CSS class name for status
 * @param {string} status - Status
 * @returns {string} CSS class name
 */
export const getStatusClassName = (status) => {
  const classMap = {
    active: 'success',
    inactive: 'secondary',
    disabled: 'danger'
  };
  return classMap[status] || 'default';
};

/**
 * Format number with commas
 * @param {number} num - Number to format
 * @returns {string} Formatted number string
 */
// export const formatNumber = (num) => {
//   if (num === null || num === undefined) return '0';
//   return num.toLocaleString();
// };

/**
 * Calculate percentage
 * @param {number} value - Value
 * @param {number} total - Total
 * @returns {number} Percentage
 */
export const calculatePercentage = (value, total) => {
  if (!total || total === 0) return 0;
  return Math.round((value / total) * 100);
};

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in ms
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

/**
 * Generate a unique ID
 * @returns {string} Unique ID
 */
export const generateId = () => {
  return Math.random().toString(36).substring(2, 11);
};

/**
 * Download data as file
 * @param {string} data - Data to download
 * @param {string} filename - File name
 * @param {string} type - MIME type
 */
export const downloadFile = (data, filename, type = 'text/csv') => {
  const blob = new Blob([data], { type });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

/**
 * Convert identities to CSV format
 * @param {Array} identities - Array of identity objects
 * @returns {string} CSV string
 */
export const identitiesToCSV = (identities) => {
  const headers = [
    'Display Name',
    'Type',
    'Status',
    'Risk Stage',
    'Risk Score',
    'UPN',
    'Department',
  ];
  
  const rows = identities.map(i => [
    i.name || '',
    i.type || '',
    i.status || '',
    i.risk.stage || '',
    i.risk.score || 0,
    i.upn || '',
    i.department || '',
  ]);
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');
  
  return csvContent;
};



// src/utils/helpers.js

export const formatNumber = (num) => {
  if (num === null || num === undefined) return "0";
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return num.toLocaleString();
  return num.toString();
};

export const downloadExcel = (user) => {
  const data = [
    {
      Username: user.username,
      Password: user.password,
    },
  ];

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, "Credentials");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const file = new Blob([excelBuffer], {
    type: "application/octet-stream",
  });

  saveAs(file, `user_credentials_${user.username}.xlsx`);
};

export const identitiesToPDF = (identities) => {
  if (!Array.isArray(identities) || identities.length === 0) {
    console.warn('No identities to export');
    return;
  }

  const doc = new jsPDF();

  // ── Header ──
  doc.setFontSize(16);
  doc.text('Identity Audit Export', 14, 20);
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Generated: ${new Date().toLocaleString()} | READ-ONLY`, 14, 28);

  // ── Table Data ──
  const headers = ['Display Name', 'Type', 'Status', 'Risk Stage', 'Risk Score', 'UPN', 'Department'];
  const rows = identities.map((i) => [
    i.name || 'N/A', i.type || 'N/A', i.status || 'N/A',
    i.risk?.stage || 'N/A', i.risk?.score ?? 'N/A', i.upn || 'N/A', i.department || 'N/A'
  ]);

  // 🔑 CRITICAL: Must be `autoTable(doc, {})` NOT `doc.autoTable({})`
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 35,
    theme: 'striped',
    styles: { fontSize: 8, cellPadding: 2, overflow: 'linebreak' },
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
    columnStyles: { 4: { halign: 'center' } },
    didDrawPage: () => {
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Page ${doc.internal.getNumberOfPages()} | READ ONLY`,
        14,
        doc.internal.pageSize.height - 10
      );
    },
  });

  const filename = `identities-export-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
};